import { DeviceMode } from '@vedalib/editor'
import type { SimulatorArea } from '@vedalib/editor/lib/elements'
import { isEmptyRtValue } from '@vedalib/rich-text'
import React, { useCallback, useState } from 'react'

import Button, { IconButton } from 'components/uiKit/Button'
import Dropdown from 'components/uiKit/Dropdown'
import Tooltip from 'components/uiKit/Dropdown/Tooltip'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import HintSettings from '../HintSettings'
import { useSimulatorContext } from '../SimulatorContext'
import ToolbarLayout from '../ToolbarLayout'
import * as s from './HintToolbar.module.scss'

interface IHintToolbarProps {
  area: SimulatorArea
  onToggleHint: () => void
  onClose: (e: React.MouseEvent) => void
  showHint: boolean
}

const HintToolbar = ({ area, onToggleHint, onClose, showHint }: IHintToolbarProps) => {
  const mode = useSimulatorContext((c) => c.mode)
  const value = useSimulatorContext((c) => c.value)
  const onChange = useSimulatorContext((c) => c.onChange)
  const [showHintSettings, setShowHintSettings] = useState(false)

  const isMobile = mode.deviceMode === DeviceMode.mobile
  const hintEmpty = isEmptyRtValue(area.hint?.text)

  const onDeleteHint = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (!value || !onChange) {
        return
      }

      const updatedItems = value.items.map((item) => {
        if (!item.areas) {
          return item
        }
        const updatedAreas = item.areas.map((a) => {
          if (a.id === area.id) {
            return { ...a, hint: undefined } as SimulatorArea
          }
          return a
        })
        return { ...item, areas: updatedAreas }
      })

      onChange({ ...value, items: updatedItems })
      onClose(e)
    },
    [area.id, onChange, onClose, value],
  )

  return (
    <ToolbarLayout>
      <div className={s.buttons}>
        <Tooltip overlay={t('uiKit.tooltip.addHint')}>
          <Button
            active={showHint}
            disabled={isMobile}
            name='simulatorHint'
            onClick={onToggleHint}
            size={KitSize.S}
            styleType='secondary'
          >
            {t('uiKit.button.hint')}
          </Button>
        </Tooltip>

        <Dropdown
          offset={[10, -12]}
          onVisibleChange={setShowHintSettings}
          overlay={<HintSettings area={area} />}
          placement='bottom-start'
          styleType='clear'
          visible={!isMobile && showHintSettings}
        >
          {() => (
            <Tooltip
              overlay={t('elements.simulator.tooltip.hintSettings')}
              trigger={['hoverWithoutContent']}
            >
              <Button
                active={showHintSettings}
                disabled={isMobile || hintEmpty}
                name='hintSettings'
                size={KitSize.S}
                styleType='secondary'
              >
                {t('common.settings')}
              </Button>
            </Tooltip>
          )}
        </Dropdown>
      </div>
      <Tooltip overlay={t('elements.simulator.tooltip.hintDelete')}>
        <IconButton
          disabled={isMobile || hintEmpty}
          icon='trashDeleteBin'
          iconSize={KitSize.S}
          name='deleteHint'
          onClick={onDeleteHint}
          size={KitSize.S}
          styleType='ghost'
        />
      </Tooltip>
    </ToolbarLayout>
  )
}

export default HintToolbar
